import { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Wallet, Plus, Upload, X, Trash2, Eye, Image as ImageIcon,
  Calendar, Phone, MapPin, FileText, Banknote, CreditCard, Landmark, Receipt,
  BarChart3, TrendingUp, TrendingDown,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend, ResponsiveContainer,
  AreaChart, Area,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const MODE_META = {
  cheque:   { label: 'Chèque',   bg: 'bg-amber-500',   text: 'text-white', Icon: Receipt },
  virement: { label: 'Virement', bg: 'bg-blue-600',    text: 'text-white', Icon: Landmark },
  especes:  { label: 'Espèces',  bg: 'bg-slate-600', text: 'text-white', Icon: Banknote },
};
const CONTRAT_TYPE_LABEL = {
  mensuel: 'Abonnement mensuel',
  trimestriel: 'Abonnement trimestriel',
  semestriel: 'Abonnement semestriel',
  annuel: 'Abonnement annuel',
  Location: 'Contrat location',
};

const fmtEUR = (n) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0) + ' FCFA';
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR'); } catch { return iso; }
};
const initials = (s = '') => s.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '•';

export default function Paiements({ user, onLogout }) {
  const [entreprises, setEntreprises] = useState([]);
  const [paiements, setPaiements] = useState([]);
  const [selectedEntreprise, setSelectedEntreprise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPreuves, setShowPreuves] = useState(null); // paiement en cours de visualisation

  const loadAll = async () => {
    setLoading(true);
    try {
      const [eRes, pRes] = await Promise.all([
        axios.get(`${API}/api/paiements/entreprises`),
        axios.get(`${API}/api/paiements`),
      ]);
      setEntreprises(eRes.data || []);
      setPaiements(pRes.data || []);
    } catch (e) {
      toast.error('Erreur chargement paiements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const todayStr = useMemo(() => new Date().toLocaleDateString('fr-FR'), []);

  return (
    <Layout user={user} onLogout={onLogout} currentPage="paiements">
      <div className="space-y-6" data-testid="paiements-root">
        {/* Bandeau vert */}
        <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 text-white px-6 py-5 shadow-lg flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center">
              <Wallet className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">Paiements entreprises</h1>
              <p className="text-slate-200 text-sm">Suivi des paiements des sociétés sous contrat</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wider text-slate-300">Situation au</p>
            <p className="text-base font-bold">{todayStr}</p>
          </div>
        </div>

        {/* 1 — Entreprises */}
        <section>
          <h2 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
            <span className="w-6 h-6 rounded-md bg-slate-700 text-white flex items-center justify-center text-xs font-bold">1</span>
            Entreprises
            <span className="text-xs font-normal text-slate-500">(cliquer sur une société pour voir sa commande)</span>
          </h2>
          {loading ? (
            <div className="h-28 flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-4 border-emerald-600" /></div>
          ) : entreprises.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-slate-500 text-sm">
                Aucune entreprise sous contrat pour l'instant.
                <br />
                <span className="text-xs">Les sociétés ayant un contrat signé apparaîtront ici.</span>
              </CardContent>
            </Card>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
              {entreprises.map((e) => {
                const isSel = selectedEntreprise?.entreprise === e.entreprise;
                return (
                  <button
                    key={e.entreprise}
                    onClick={() => setSelectedEntreprise(e)}
                    className={`min-w-[220px] flex-shrink-0 text-left rounded-xl border-2 bg-white px-4 py-3 transition shadow-sm hover:shadow-md ${
                      isSel ? 'border-slate-700 ring-2 ring-slate-200' : 'border-slate-200'
                    }`}
                    data-testid={`entreprise-card-${e.entreprise}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-9 h-9 rounded-md bg-slate-700 text-white flex items-center justify-center font-bold text-xs">
                        {initials(e.entreprise)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-slate-900 text-sm truncate leading-tight">{e.entreprise}</p>
                        <p className="text-[11px] text-slate-500 truncate">{CONTRAT_TYPE_LABEL[e.type_contrat] || e.type_contrat || '—'}</p>
                      </div>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mb-1">
                      <div className="h-full bg-slate-600" style={{ width: `${Math.min(100, e.pct_paye)}%` }}></div>
                    </div>
                    <div className="flex justify-between items-baseline text-[11px]">
                      <span className="text-slate-500">Payé <span className="font-bold text-slate-700">{fmtEUR(e.montant_paye)}</span></span>
                      <span className="font-bold text-slate-700">{e.pct_paye} %</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* 2 — Fiche entreprise sélectionnée */}
        {selectedEntreprise && (
          <section>
            <h2 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
              <span className="w-6 h-6 rounded-md bg-slate-700 text-white flex items-center justify-center text-xs font-bold">2</span>
              Fiche de l'entreprise&nbsp;: <span className="text-slate-900">{selectedEntreprise.entreprise}</span>
            </h2>
            <Card className="border-2 border-slate-700 rounded-2xl">
              <CardContent className="p-5">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    {selectedEntreprise.adresse && (
                      <p className="text-xs text-slate-500 flex items-center gap-1 mb-3">
                        <MapPin className="h-3 w-3" /> {selectedEntreprise.adresse}
                      </p>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <InfoBox label="Type de contrat" value={CONTRAT_TYPE_LABEL[selectedEntreprise.type_contrat] || selectedEntreprise.type_contrat || '—'} />
                      <InfoBox label="Mode préféré" value={MODE_META[selectedEntreprise.mode_prefere]?.label || '—'} />
                      <InfoBox label="Date de début" value={fmtDate(selectedEntreprise.date_debut)} />
                      <InfoBox label="Date de fin" value={fmtDate(selectedEntreprise.date_fin)} />
                      <InfoBox label="Montant du contrat" value={fmtEUR(selectedEntreprise.montant_contrat)} />
                      <InfoBox label="Reste à payer" value={fmtEUR(selectedEntreprise.reste_a_payer)} highlight={selectedEntreprise.reste_a_payer > 0} />
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-slate-700 mb-2">Choix de la commande</p>
                    <div className="grid grid-cols-2 gap-2">
                      {(selectedEntreprise.commandes || []).slice(0, 6).map((c, i) => (
                        <div key={i} className="bg-slate-100 border border-slate-200 text-slate-800 rounded-lg px-3 py-2 text-xs font-semibold text-center">
                          {c}
                        </div>
                      ))}
                      {!selectedEntreprise.commandes?.length && (
                        <p className="text-xs text-slate-400 italic col-span-2">Aucune commande détaillée</p>
                      )}
                    </div>

                    {(selectedEntreprise.contact_nom || selectedEntreprise.contact_telephone) && (
                      <>
                        <p className="text-xs font-semibold text-slate-700 mt-4 mb-1">Contact</p>
                        <p className="text-sm font-bold text-slate-900">
                          {selectedEntreprise.contact_nom || '—'}
                          {selectedEntreprise.contact_telephone && (
                            <span className="font-normal text-slate-600"> · {selectedEntreprise.contact_telephone}</span>
                          )}
                        </p>
                      </>
                    )}

                    <div className="flex justify-end mt-4">
                      <Button
                        onClick={() => {
                          const el = document.getElementById('paiements-table-section');
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        className="bg-slate-700 hover:bg-slate-800 gap-2"
                        data-testid="btn-voir-tableau"
                      >
                        Voir dans le tableau
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* 3 — Tableau des paiements */}
        <section id="paiements-table-section">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <span className="w-6 h-6 rounded-md bg-slate-700 text-white flex items-center justify-center text-xs font-bold">3</span>
              Tableau des paiements
            </h2>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-slate-700 hover:bg-slate-800 gap-2"
              data-testid="btn-nouveau-paiement"
            >
              <Plus className="h-4 w-4" /> Nouveau paiement
            </Button>
          </div>

          <Card className="rounded-2xl overflow-hidden">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-700 text-white text-left text-xs uppercase tracking-wide">
                      <th className="py-3 px-4">Entreprise</th>
                      <th className="py-3 px-4">Mode</th>
                      <th className="py-3 px-4">Référence</th>
                      <th className="py-3 px-4">Date</th>
                      <th className="py-3 px-4 text-right">Montant</th>
                      <th className="py-3 px-4">Preuve</th>
                      <th className="py-3 px-4 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paiements.length === 0 && (
                      <tr><td colSpan={7} className="py-10 text-center text-slate-400">Aucun paiement enregistré</td></tr>
                    )}
                    {paiements.map((p) => {
                      const meta = MODE_META[p.mode] || MODE_META.especes;
                      const highlight = selectedEntreprise && p.entreprise === selectedEntreprise.entreprise;
                      return (
                        <tr
                          key={p.id}
                          className={`border-b border-slate-100 ${highlight ? 'bg-slate-100/50' : 'hover:bg-slate-50/60'}`}
                          data-testid={`paiement-row-${p.id.substring(0, 8)}`}
                        >
                          <td className="py-3 px-4 font-medium text-slate-800">{p.entreprise}</td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center justify-center text-xs font-bold px-3 py-1 rounded-full ${meta.bg} ${meta.text} min-w-[80px]`}>
                              {meta.label}
                            </span>
                          </td>
                          <td className="py-3 px-4 font-mono text-xs text-slate-700">{p.reference || '—'}</td>
                          <td className="py-3 px-4 text-slate-600">{fmtDate(p.date)}</td>
                          <td className="py-3 px-4 text-right font-extrabold text-slate-900">{fmtEUR(p.montant)}</td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => setShowPreuves(p)}
                              className={`text-xs font-semibold px-3 py-1 rounded-full border-2 ${
                                (p.preuves || []).length > 0
                                  ? 'border-emerald-600 text-slate-700 hover:bg-slate-100'
                                  : 'border-rose-500 text-rose-600 hover:bg-rose-50'
                              }`}
                              data-testid={`btn-preuve-${p.id.substring(0, 8)}`}
                            >
                              {(p.preuves || []).length > 0 ? `Preuve (${p.preuves.length})` : '+ Preuve'}
                            </button>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Supprimer le paiement de ${fmtEUR(p.montant)} ?`)) return;
                                try {
                                  await axios.delete(`${API}/api/paiements/${p.id}`);
                                  toast.success('Paiement supprimé');
                                  loadAll();
                                } catch { toast.error('Erreur suppression'); }
                              }}
                              className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50"
                              title="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* 4 — Graphiques analytiques */}
        <PaiementsAnalytics paiements={paiements} entreprises={entreprises} />
      </div>

      {/* Formulaire nouveau paiement */}
      <NouveauPaiementSheet
        open={showForm}
        onOpenChange={setShowForm}
        entreprises={entreprises}
        preselect={selectedEntreprise}
        onCreated={() => { setShowForm(false); loadAll(); }}
      />

      {/* Preuves de paiement */}
      <PreuvesSheet
        paiement={showPreuves}
        onClose={() => setShowPreuves(null)}
        onChanged={loadAll}
      />
    </Layout>
  );
}

// ───────── Composants ─────────
function InfoBox({ label, value, highlight }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
      <p className={`text-sm font-extrabold leading-tight mt-0.5 ${highlight ? 'text-rose-600' : 'text-slate-900'}`}>{value}</p>
    </div>
  );
}

function NouveauPaiementSheet({ open, onOpenChange, entreprises, preselect, onCreated }) {
  const [form, setForm] = useState({
    entreprise: '', contrat_id: '', mode: 'cheque',
    reference: '', date: new Date().toISOString().slice(0, 10),
    montant: '', commentaire: '',
  });
  const [preuvesFiles, setPreuvesFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({
        entreprise: preselect?.entreprise || '',
        contrat_id: preselect?.contrats?.[0]?.id || '',
        mode: preselect?.mode_prefere || 'cheque',
        reference: '',
        date: new Date().toISOString().slice(0, 10),
        montant: '',
        commentaire: '',
      });
      setPreuvesFiles([]);
    }
  }, [open, preselect]);

  const selectedEnt = entreprises.find(e => e.entreprise === form.entreprise);

  const submit = async () => {
    if (!form.entreprise) { toast.error('Choisissez une entreprise'); return; }
    if (!form.montant || parseFloat(form.montant) <= 0) { toast.error('Montant invalide'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/paiements`, {
        ...form,
        montant: parseFloat(form.montant),
      });
      const pid = res.data.id;
      // Upload préuves (si fournies)
      for (const f of preuvesFiles) {
        const fd = new FormData();
        fd.append('file', f);
        await axios.post(`${API}/api/paiements/${pid}/preuves`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success('Paiement enregistré ✓');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-5 overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle className="text-xl flex items-center gap-2">
            <Wallet className="h-5 w-5 text-slate-700" />
            Nouveau paiement
          </SheetTitle>
          <p className="text-xs text-slate-500">Seules les entreprises ayant un contrat signé peuvent payer.</p>
        </SheetHeader>

        <div className="space-y-4">
          <div>
            <Label>Entreprise <span className="text-rose-500">*</span></Label>
            <Select
              value={form.entreprise}
              onValueChange={(v) => {
                const ent = entreprises.find(e => e.entreprise === v);
                setForm({
                  ...form,
                  entreprise: v,
                  contrat_id: ent?.contrats?.[0]?.id || '',
                  mode: ent?.mode_prefere || form.mode,
                });
              }}
            >
              <SelectTrigger data-testid="select-entreprise"><SelectValue placeholder="Choisir une société sous contrat" /></SelectTrigger>
              <SelectContent>
                {entreprises.map(e => (
                  <SelectItem key={e.entreprise} value={e.entreprise}>
                    {e.entreprise} — Reste {fmtEUR(e.reste_a_payer)}
                  </SelectItem>
                ))}
                {entreprises.length === 0 && <SelectItem value="__none__" disabled>Aucune entreprise sous contrat</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          {/* Récap entreprise sélectionnée */}
          {selectedEnt && (
            <div className="bg-slate-100 border border-slate-200 rounded-lg p-3 text-xs space-y-1">
              <div className="flex justify-between"><span className="text-slate-600">Type :</span><b>{CONTRAT_TYPE_LABEL[selectedEnt.type_contrat] || selectedEnt.type_contrat}</b></div>
              <div className="flex justify-between"><span className="text-slate-600">Période :</span><b>{fmtDate(selectedEnt.date_debut)} → {fmtDate(selectedEnt.date_fin)}</b></div>
              <div className="flex justify-between"><span className="text-slate-600">Montant contrat :</span><b>{fmtEUR(selectedEnt.montant_contrat)}</b></div>
              <div className="flex justify-between"><span className="text-slate-600">Déjà payé :</span><b className="text-slate-700">{fmtEUR(selectedEnt.montant_paye)}</b></div>
              <div className="flex justify-between"><span className="text-slate-600">Reste à payer :</span><b className="text-rose-600">{fmtEUR(selectedEnt.reste_a_payer)}</b></div>
            </div>
          )}

          <div>
            <Label>Mode de paiement <span className="text-rose-500">*</span></Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {Object.entries(MODE_META).map(([k, m]) => {
                const Icon = m.Icon;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setForm({ ...form, mode: k })}
                    className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border-2 transition ${
                      form.mode === k ? `${m.bg} ${m.text} border-transparent` : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`mode-${k}`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-xs font-semibold">{m.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Référence</Label>
              <Input
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder={form.mode === 'cheque' ? 'N° chèque' : form.mode === 'virement' ? 'Réf. virement' : 'N° reçu'}
              />
            </div>
            <div>
              <Label>Date <span className="text-rose-500">*</span></Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
          </div>

          <div>
            <Label>Montant (FCFA) <span className="text-rose-500">*</span></Label>
            <Input
              type="number"
              min="0"
              value={form.montant}
              onChange={(e) => setForm({ ...form, montant: e.target.value })}
              placeholder="Ex: 500000"
              data-testid="input-montant"
            />
          </div>

          <div>
            <Label>Commentaire</Label>
            <Textarea
              rows={2}
              value={form.commentaire}
              onChange={(e) => setForm({ ...form, commentaire: e.target.value })}
              placeholder="Note interne (optionnel)"
            />
          </div>

          {/* Preuves à l'avance */}
          <div>
            <Label>Preuves (chèque scanné, reçu, virement…)</Label>
            <div className="mt-1 border-2 border-dashed border-slate-300 rounded-lg p-3 bg-slate-50/50">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                multiple
                hidden
                onChange={(e) => setPreuvesFiles([...preuvesFiles, ...Array.from(e.target.files || [])])}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-slate-700 py-2"
              >
                <Upload className="h-4 w-4" /> Ajouter une photo ou PDF
              </button>
              {preuvesFiles.length > 0 && (
                <ul className="mt-2 text-xs text-slate-700 space-y-1">
                  {preuvesFiles.map((f, i) => (
                    <li key={i} className="flex items-center justify-between bg-white rounded px-2 py-1 border border-slate-200">
                      <span className="truncate">{f.name}</span>
                      <button
                        onClick={() => setPreuvesFiles(preuvesFiles.filter((_, j) => j !== i))}
                        className="text-rose-500 hover:text-rose-700"
                      ><X className="h-3 w-3" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-slate-700 hover:bg-slate-800" data-testid="btn-submit-paiement">
              {saving ? 'Enregistrement…' : 'Enregistrer le paiement'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function PreuvesSheet({ paiement, onClose, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [zoom, setZoom] = useState(null);
  const fileInputRef = useRef(null);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', f);
        await axios.post(`${API}/api/paiements/${paiement.id}/preuves`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success('Preuve(s) ajoutée(s) ✓');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  const removePreuve = async (index) => {
    try {
      await axios.delete(`${API}/api/paiements/${paiement.id}/preuves/${index}`);
      toast.success('Preuve supprimée');
      onChanged();
    } catch { toast.error('Erreur suppression'); }
  };

  if (!paiement) return null;
  const meta = MODE_META[paiement.mode] || MODE_META.especes;
  // Rafraîchit depuis la liste parent
  const freshPaiement = paiement;
  const preuves = freshPaiement.preuves || [];

  return (
    <>
      <Sheet open={!!paiement} onOpenChange={(o) => { if (!o) onClose(); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl p-5 overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-xl flex items-center gap-2">
              <span className="w-6 h-6 rounded-md bg-slate-700 text-white flex items-center justify-center text-xs font-bold">4</span>
              Preuves de paiement
            </SheetTitle>
            <p className="text-xs text-slate-500">
              <b>{paiement.entreprise}</b> · <span className={`font-bold ${meta.bg === 'bg-amber-500' ? 'text-amber-600' : meta.bg === 'bg-blue-600' ? 'text-blue-600' : 'text-slate-700'}`}>{meta.label}</span> · {fmtEUR(paiement.montant)}
            </p>
          </SheetHeader>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {preuves.map((pr, i) => {
              const isImage = (pr.content_type || '').startsWith('image/');
              const url = `${API}/api/paiements/preuves/file/${paiement.id}/${i}`;
              const auth = `?t=${Date.now()}`;
              return (
                <div key={i} className="relative group rounded-xl overflow-hidden border border-slate-200 bg-white aspect-square">
                  {isImage ? (
                    <AuthenticatedImage
                      src={url}
                      alt={pr.filename}
                      className="w-full h-full object-cover cursor-zoom-in"
                      onClick={() => setZoom({ ...pr, index: i })}
                    />
                  ) : (
                    <a href={url} target="_blank" rel="noreferrer" className="w-full h-full flex flex-col items-center justify-center gap-2 bg-slate-50 hover:bg-slate-100">
                      <FileText className="h-10 w-10 text-slate-400" />
                      <span className="text-[10px] text-slate-600 truncate px-2">{pr.filename}</span>
                    </a>
                  )}
                  <button
                    onClick={() => removePreuve(i)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition"
                    title="Supprimer"
                    data-testid={`btn-delete-preuve-${i}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}

            {/* + ajouter */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="aspect-square rounded-xl border-2 border-dashed border-slate-400 flex flex-col items-center justify-center gap-2 text-slate-700 hover:bg-slate-100 transition disabled:opacity-50"
              data-testid="btn-add-preuve"
            >
              <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                <Plus className="h-5 w-5" />
              </div>
              <span className="text-xs font-semibold">{uploading ? 'Envoi…' : 'Ajouter des photos'}</span>
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            hidden
            onChange={(e) => { upload(e.target.files); e.target.value = ''; }}
          />

          <div className="mt-5 bg-slate-50 rounded-lg p-3 text-xs text-slate-600 space-y-1">
            <p className="font-semibold text-slate-700 mb-1">Dans cette fenêtre :</p>
            <p>✓ Ajouter une ou plusieurs photos</p>
            <p>✓ Supprimer avec la croix rouge</p>
            <p>✓ Toucher une photo pour l'agrandir</p>
            <p>✓ Chèque, preuve de virement ou reçu</p>
          </div>
        </SheetContent>
      </Sheet>

      {/* Zoom image */}
      <Dialog open={!!zoom} onOpenChange={(o) => { if (!o) setZoom(null); }}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black/90">
          {zoom && (
            <AuthenticatedImage
              src={`${API}/api/paiements/preuves/file/${paiement.id}/${zoom.index}`}
              alt={zoom.filename}
              className="w-full h-auto max-h-[85vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// Image qui charge le fichier avec le token Bearer (axios interceptor pose déjà le header)
function AuthenticatedImage({ src, alt, className, onClick }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(src, { responseType: 'blob' });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        setBlobUrl(url);
      } catch {
        if (!cancelled) setBlobUrl(null);
      }
    })();
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line
  }, [src]);
  if (!blobUrl) return <div className={`${className} bg-slate-100 flex items-center justify-center text-slate-400`}><ImageIcon className="h-6 w-6" /></div>;
  return <img src={blobUrl} alt={alt} className={className} onClick={onClick} />;
}

// ═══════════════════════════════════════════════════════════════
// 4 — Tableaux d'évolution (style Suivi)
// ═══════════════════════════════════════════════════════════════
function buildMonthlySeries(items, dateKey = 'date', valueKey = 'montant') {
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('fr-FR', { month: 'short' }),
      count: 0,
      value: 0,
    });
  }
  items.forEach((p) => {
    const dRaw = p[dateKey];
    if (!dRaw) return;
    const dt = new Date(dRaw);
    const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
    const m = months.find(x => x.key === key);
    if (!m) return;
    m.count += 1;
    m.value += parseFloat(p[valueKey] || 0);
  });
  return months;
}

function computeTrend(series, field = 'value') {
  const total = series.reduce((s, p) => s + (p[field] || 0), 0);
  const last = series[series.length - 1]?.[field] || 0;
  const prev = series[series.length - 2]?.[field] || 0;
  let pct = 0;
  if (prev > 0) pct = Math.round(((last - prev) / prev) * 100);
  else if (last > 0) pct = 100;
  return { total, last, prev, pct };
}

function PaiementsAnalytics({ paiements, entreprises }) {
  // 4 séries d'évolution style Suivi
  const stats = useMemo(() => {
    const cheques   = paiements.filter(p => p.mode === 'cheque');
    const virements = paiements.filter(p => p.mode === 'virement');
    const especes   = paiements.filter(p => p.mode === 'especes');
    return [
      { title: 'Évolution des paiements',  color: '#2563eb', emoji: '💳', unit: 'FCFA',  series: buildMonthlySeries(paiements) },
      { title: 'Évolution des chèques',    color: '#f59e0b', emoji: '📝', unit: 'FCFA',  series: buildMonthlySeries(cheques) },
      { title: 'Évolution des virements',  color: '#2563eb', emoji: '🏦', unit: 'FCFA',  series: buildMonthlySeries(virements) },
      { title: 'Évolution des espèces',    color: '#059669', emoji: '💵', unit: 'FCFA',  series: buildMonthlySeries(especes) },
    ];
  }, [paiements]);

  // KPIs résumé
  const totalPaye = paiements.reduce((s, p) => s + parseFloat(p.montant || 0), 0);
  const totalDu   = entreprises.reduce((s, e) => s + (e.montant_contrat || 0), 0);
  const pctGlobal = totalDu > 0 ? Math.round((totalPaye / totalDu) * 100) : 0;

  return (
    <section className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <span className="w-6 h-6 rounded-md bg-slate-700 text-white flex items-center justify-center text-xs font-bold">4</span>
          Statistiques & tendances
          <BarChart3 className="h-4 w-4 text-slate-500" />
        </h2>
        <span className="text-xs text-slate-500">Évolution sur les 6 derniers mois</span>
      </div>

      {/* KPIs résumé */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KpiMini label="Total encaissé" value={fmtEUR(totalPaye)} accent="emerald" icon={Wallet} />
        <KpiMini label="Montant contractuel" value={fmtEUR(totalDu)} accent="slate" icon={FileText} />
        <KpiMini label="Avancement global" value={`${pctGlobal} %`} accent="blue" icon={TrendingUp} />
        <KpiMini label="Nb paiements" value={paiements.length} accent="amber" icon={BarChart3} />
      </div>

      {/* 4 tableaux compacts style Suivi */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {stats.map((s) => {
          const t = computeTrend(s.series, 'value');
          const positive = t.pct >= 0;
          return (
            <div
              key={s.title}
              className="border border-slate-200 rounded-xl p-3 bg-white shadow-sm hover:shadow-md transition"
              data-testid={`stat-${s.title}`}
            >
              <div className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1">
                <span>{s.emoji}</span>
                {s.title}
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-xl font-black text-slate-900 truncate">{fmtEUR(t.total)}</span>
                <span className={`text-[11px] font-bold whitespace-nowrap ${positive ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {positive
                    ? <TrendingUp className="h-3 w-3 inline" />
                    : <TrendingDown className="h-3 w-3 inline" />}
                  {' '}{positive ? '+' : ''}{t.pct}%
                </span>
              </div>
              <div className="h-14 -mx-1">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={s.series} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id={`grad-${s.title.replace(/\s+/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={s.color} stopOpacity={0.4} />
                        <stop offset="100%" stopColor={s.color} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="label" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} interval={0} />
                    <ReTooltip
                      contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #e5e7eb' }}
                      labelFormatter={(l) => `Mois : ${l}`}
                      formatter={(v) => [fmtEUR(v), 'Montant']}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={s.color}
                      strokeWidth={2}
                      fill={`url(#grad-${s.title.replace(/\s+/g, '')})`}
                      dot={{ r: 2, fill: s.color }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className={`text-[10px] mt-1 ${positive ? 'text-emerald-600' : 'text-slate-500'}`}>
                {t.last === 0 ? 'Aucun paiement ce mois-ci' :
                  positive ? `Hausse ce mois-ci (${fmtEUR(t.last)})` : `Baisse ce mois-ci (${fmtEUR(t.last)})`}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function KpiMini({ label, value, accent = 'slate', icon: Icon }) {
  const palette = {
    emerald: 'from-emerald-500 to-emerald-700',
    slate:   'from-slate-500 to-slate-700',
    blue:    'from-blue-500 to-blue-700',
    amber:   'from-amber-500 to-amber-700',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 shadow-sm">
      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${palette[accent]} flex items-center justify-center text-white`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xl font-extrabold text-slate-900 leading-none truncate">{value}</p>
        <p className="text-[11px] text-slate-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
}
